import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import LoadingContent from "../../components/LoadingContent";
import DriverStanding from "../../components/Standings/DriverStanding";
import TeamStanding from "../../components/Standings/TeamStanding";
import DriverStats from "../../components/Standings/DriverStats";
import TeamStats from "../../components/Standings/TeamStats";
import Scoring from "../../components/Standings/Scoring";
import ErrorPage from "../ErrorPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactSlider from "react-slider";
import {
  faArrowCircleLeft,
  faArrowCircleRight,
  faBackwardStep,
  faForwardStep,
  faPlay,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

const Standings = ({ season }: { season?: string }) => {
  const { t } = useTranslation();
  const [standingsData, setStandingsData] = useState<StandingsData>();
  const [error, setError] = useState(false);
  const [slider, setSlider] = useState(0);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false); // Estado para el reproductor automático

  useEffect(() => {
    setSlider(0);
    setSliderIndex(0);
  }, [season]);

  // Variable para base URL limpia
  const API_URL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5500"
      : "https://ligavirtualf1.es:5500";

  // Cuando cambia la temporada, reseteamos slider para que el backend elija el último
  useEffect(() => {
    setSlider(0);
  }, [season]);

  // Único efecto para traer datos (primera carga y al mover slider)
  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, behavior: "smooth" });

    const handler = setTimeout(() => {
      // Definimos el tipo explícito para params
      const params: { season?: string; grand_prix_event_id?: number | string } =
        { season };

      if (slider) {
        params.grand_prix_event_id = slider;
      }

      axios
        .get(`${API_URL}/api/championship/detailedStandingsGP`, { params })
        .then(({ data }) => {
          setStandingsData(data);

          if (data.results && data.results.length > 0) {
            const lastId = data.results[data.results.length - 1].id;
            if (!slider) {
              const currentId = data.current_gp_id || lastId;
              setSlider(currentId);

              const index = data.results.findIndex(
                (gp: any) => gp.id === currentId,
              );

              setSliderIndex(index);
            }
          }

          document.title = "LVF1 - " + data.champ_data.name;
          setLoading(false);
        })
        .catch(({ response }) => {
          setError(true);
          console.error(response?.data || response);
        });
    }, 300);

    return () => clearTimeout(handler);
  }, [slider, season]);

  // 3. Efecto para manejar el avance automático cada 2 segundos cuando isPlaying es true

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isPlaying) {
      interval = setInterval(() => {
        if (standingsData != null && standingsData.results != null) {
          setSliderIndex((prevIndex) => {
            const nextIndex = prevIndex + 1;

            if (nextIndex >= standingsData.results.length) {
              setIsPlaying(false);
              return prevIndex;
            }

            // Actualizamos también el ID del slider para que fetchee los datos
            setSlider(standingsData.results[nextIndex].id);
            return nextIndex;
          });
        }
      }, 2000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isPlaying, standingsData]);

  const goToIndex = (index: number) => {
    if (standingsData && standingsData.results[index]) {
      setSliderIndex(index);
      setSlider(standingsData.results[index].id);
    }
  };
  // Funciones de control de reproducción
  const handleGoToStart = () => {
    setIsPlaying(false);
    goToIndex(0); // Va al primer elemento (índice 0)
  };

  const handleGoToEnd = () => {
    setIsPlaying(false);
    if (standingsData && standingsData.results.length > 0) {
      goToIndex(standingsData.results.length - 1); // Va al último elemento
    }
  };

  const handleTogglePlay = () => {
    if (
      !isPlaying &&
      standingsData != null &&
      sliderIndex >= standingsData.results.length - 1
    ) {
      goToIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  if (error) return <ErrorPage />;
  if (standingsData == null || standingsData.results == null)
    return <LoadingContent />;

  const currentIndex = standingsData.results.findIndex(
    (gp) => gp.id === slider,
  );
  if (standingsData.results[currentIndex] == null) return <LoadingContent />;
  return (
    <div className="full-height padding-md app-container standingsdiv-container page-transition">
      <div className="standings-title">
        {standingsData.prev_season != null && (
          <Link to={`${t("routes:Season")}/${standingsData.prev_season}`}>
            <FontAwesomeIcon icon={faArrowCircleLeft} />
          </Link>
        )}
        {standingsData.champ_data.name}
        {standingsData.next_season != null && (
          <Link to={`${t("routes:Season")}/${standingsData.next_season}`}>
            <FontAwesomeIcon icon={faArrowCircleRight} />
          </Link>
        )}
      </div>

      {/* Controles de Reproducción y Slider */}
      <div className="timeline-container timeline-containerstandings">
        {loading && <LoadingContent />}
        <div className="playback-controls flex">
          <button onClick={handleGoToStart}>
            <FontAwesomeIcon icon={faBackwardStep} />
          </button>

          <button onClick={handleTogglePlay}>
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>

          <button onClick={handleGoToEnd}>
            <FontAwesomeIcon icon={faForwardStep} />
          </button>
        </div>
        <div style={{ width: standingsData.results.length * 55 }}>
          <ReactSlider
            className="gp-slider"
            thumbClassName="gp-thumb"
            trackClassName="gp-track"
            value={sliderIndex}
            min={0}
            max={standingsData.results.length - 1}
            step={1}
            onChange={(index) => {
              setSliderIndex(index);
              setSlider(standingsData.results[index].id);
            }}
          />
          <div className="gp-flags">
            {standingsData.results.map((gp, index) => (
              <div
                key={gp.id}
                onClick={() => {
                  setSlider(gp.id);
                  setSliderIndex(index);
                }}
                className={`${index === sliderIndex ? "selected" : ""} ${index <= currentIndex ? "active" : ""}`}
              >
                <img
                  src={`/assets/Flags/${gp.code}.png`}
                  alt={gp.flag_name}
                  className={`gp-flag flag`}
                  title={gp.name}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <DriverStanding standingsData={standingsData} />
      <br></br>
      {standingsData.has_teams === 1 && (
        <>
          <TeamStanding standingsData={standingsData} />
          <br></br>
        </>
      )}
      <DriverStats data={standingsData} champData={standingsData.champ_data} />
      <br></br>

      {standingsData.has_teams === 1 && (
        <>
          <TeamStats
            data={standingsData}
            champData={standingsData.champ_data}
          />
          <br></br>
        </>
      )}
      <Scoring scorings={standingsData.scorings} />
    </div>
  );
};

export default Standings;
